import {useEffect} from 'react';
import clsx from 'clsx';
import {emitEventLog} from '@di-shared/features/analytics';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {ItemProps} from '@di-shared/features/safe-driving-vDSP/SafeDrivingMonthlyGraph/index';
import useHorizontalScroll from '@di-shared/utils/useHorizontalScroll';

interface SafeDrivingMonthlyGraphEuProps {
  dashedLineScore: number;
  coloredItemScoreThreshold?: number;
  items: Omit<ItemProps, 'isActive' | 'onClick'>[];
}

const SCALE_FACTOR = 1.2;

export default function ({dashedLineScore, items, coloredItemScoreThreshold}: SafeDrivingMonthlyGraphEuProps) {
  const {t} = useInferableTranslation();
  const {scrollRef, scrollToStart, scrollToEnd, isAtLeft, isScrollable, isAtRight} = useHorizontalScroll<HTMLDivElement>();
  const isDataLessThanSix = items.length < 6;

  useEffect(() => {
    scrollToEnd();
  }, []);

  return (
    <div className={clsx('monthly_graph_scroll_wrap', {type_center: isDataLessThanSix})}>
      <div
        className="bar_graph_average"
        style={{bottom: 26 + dashedLineScore * SCALE_FACTOR}}
      >
        <span className="average_line"></span>
        <span className="average_text_box">
          <span className="average_text">{t('ctn_dss_points_$score_eu', {score: dashedLineScore})}</span>
        </span>
      </div>

      <div
        className="statistics_graph_area"
        ref={scrollRef}
      >
        <div
          className={clsx('bar_graph', {is_center: isDataLessThanSix})}
          style={{}}
        >
          {items.map(({score, label}) => (
            <Item
              key={label}
              score={score}
              label={label}
              isActive={(!!score || score === 0) && score >= (coloredItemScoreThreshold ?? Infinity)}
            />
          ))}
        </div>
      </div>
      {isScrollable && !isAtLeft && (
        <button
          type="button"
          className="btn_prev"
          onClick={() => {
            emitEventLog({
              event: 'di_driveinfo_interaction',
              data_action: 'slide banner',
              data_name: t('ctn_dss_monthly_score_title_eu'),
            });
            scrollToStart();
          }}
        >
          <span className="ic ic_arrow_back_16px"></span>
        </button>
      )}
      {isScrollable && !isAtRight && (
        <button
          type="button"
          className="btn_next"
          onClick={() => {
            emitEventLog({
              event: 'di_driveinfo_interaction',
              data_action: 'slide banner',
              data_name: t('ctn_dss_monthly_score_title_eu'),
            });
            scrollToEnd();
          }}
        >
          <span
            className="ic ic_arrow_forward_16px"
            aria-hidden="true"
          ></span>
        </button>
      )}
    </div>
  );
}

function Item({score = 0, label = '', isActive = false, onClick = () => {}}: ItemProps): JSX.Element {
  return (
    <div
      className={clsx('bar_graph_item', {'is-active': isActive})}
      onClick={onClick}
    >
      <span className="graph_score">{score}</span>
      <span
        className="graph_bar"
        style={{height: (score ?? 0) * SCALE_FACTOR}}
      ></span>
      <span
        className="graph_title"
        style={{whiteSpace: 'pre-wrap'}}
      >
        {label}
      </span>
    </div>
  );
}
